"use client";

import { Button } from "@components/core/Button";
import * as m from "motion/react-m";
import { AnimatePresence } from "motion/react";
import { memo, useEffect, useRef } from "react";
import { Mic as MicIcon } from "lucide-react";
import { Noise } from "@ui/components/core/Noise/Noise";
import clsx from "clsx";
import { useTranslations } from "next-intl";
import { Link } from "@ui/components/core/Link";
import { appUrls } from "@/constants";
import { CircleArrowUp } from "lucide-react";
import { ICON_ALIGN_OPTIONS } from "@ui/tokens/icon-types";
import { LimitReachedOverlay } from "../LimitReachedOverlay";
import { useSTTRealtime, type RecordingStatus } from "./useSTTRealtime";

// UI Components
const StatusIndicator = memo(({ status }: { status: RecordingStatus }) => {
  const t = useTranslations("ClientKeys.SpeechToText.realtime");

  const getStatusText = () => {
    switch (status) {
      case "idle":
        return t("status.ready");
      case "requesting_permission":
        return t("status.requesting_permission");
      case "setting_up_audio":
        return t("status.setting_up_audio");
      case "connecting":
        return t("status.connecting");
      case "recording":
        return t("status.recording");
      case "finished":
        return t("status.finished");
      default:
        return t("status.ready");
    }
  };

  return (
    <div className='flex items-center gap-8'>
      <div
        className={clsx(
          "flex h-16 w-16 items-center justify-center rounded-full",
          status === "recording" ? "bg-[#1A8C13]" : "bg-dark",
        )}
      >
        <div className='h-8 w-8 rounded-full bg-default' />
      </div>
      <AnimatePresence mode='wait'>
        <m.span
          key={status}
          className='f-paragraph-02'
          initial={{ opacity: 0, filter: "blur(4px)" }}
          animate={{ opacity: 1, filter: "blur(0px)" }}
          exit={{ opacity: 0, filter: "blur(4px)" }}
          transition={{ duration: 0.2 }}
        >
          {getStatusText()}
        </m.span>
      </AnimatePresence>
    </div>
  );
});
StatusIndicator.displayName = "StatusIndicator";

const TranscriptText = memo(({ text, isPartial = false }: { text: string; isPartial?: boolean }) => {
  if (!isPartial) {
    return <span className='leading-relaxed'>{text}</span>;
  }

  const words = text.split(/(\s+)/);

  return (
    <span className='leading-relaxed opacity-70'>
      {words.map((word, index) => {
        if (word.trim() === "") return word;

        return (
          <m.span
            key={`${index}-${word}`}
            initial={{ filter: "blur(4px)", opacity: 0 }}
            animate={{ filter: "blur(0px)", opacity: 1 }}
            transition={{ duration: 0.1 }}
            className='inline-block'
          >
            {word}
          </m.span>
        );
      })}
    </span>
  );
});

TranscriptText.displayName = "TranscriptText";

const SessionLimitMessage = memo(({ onClose }: { onClose: () => void }) => {
  const t = useTranslations("ClientKeys.SpeechToText.realtime");

  const finalUrl = `${appUrls.speechToText}?dialog=scribe-v2-realtime-demo`;

  return (
    <div className='flex h-full flex-col items-center justify-center px-24 text-center'>
      <div className='mx-auto max-w-md space-y-24'>
        <div className='space-y-8'>
          <h3 className='f-heading-06'>{t("session_limit.title")}</h3>
          <p className='f-paragraph-02 text-light'>{t("session_limit.description")}</p>
        </div>
        <div className='flex items-center justify-center gap-12'>
          <Link asButton buttonVariants={{ size: "md" }} href={finalUrl}>
            {t("session_limit.sign_up")}
          </Link>
          <Button onClick={onClose} variant='elevated' size='md'>
            {t("session_limit.close")}
          </Button>
        </div>
      </div>
    </div>
  );
});
SessionLimitMessage.displayName = "SessionLimitMessage";

const TranscriptDisplay = ({
  committedTranscript,
  partialTranscript,
  status,
}: {
  committedTranscript: string;
  partialTranscript: string;
  status: RecordingStatus;
}) => {
  const t = useTranslations("ClientKeys.SpeechToText.realtime");
  const scrollRef = useRef<HTMLDivElement>(null);
  // Auto-scroll to bottom as new text arrives with smooth behavior
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [committedTranscript, partialTranscript]);

  if (!committedTranscript && !partialTranscript) {
    const isConnecting = status !== "recording";
    if (status === "finished") {
      return null;
    }

    return (
      <div className='f-description-01 mx-auto flex h-full max-w-lg items-center justify-center px-24 text-center text-lighter'>
        {isConnecting ? t("loading") : t("speak_to_transcribe")}
      </div>
    );
  }
  return (
    <div className='relative flex h-full items-center justify-center'>
      {/* Fade gradient at top */}
      <div className='pointer-events-none absolute left-0 right-0 top-0 z-10 h-48 bg-gradient-to-b from-[white] to-transparent' />
      {/* Scrollable transcript container */}
      <div
        ref={scrollRef}
        className='scrollbar-thin flex h-full w-full flex-col items-center overflow-y-auto scroll-smooth px-24 py-60'
      >
        {/* Transcript content */}
        <div className='f-description-01 w-full max-w-lg space-y-8 text-left'>
          {committedTranscript && <TranscriptText text={committedTranscript} />}
          {committedTranscript && partialTranscript && <span> </span>}
          {partialTranscript && <TranscriptText text={partialTranscript} isPartial />}
        </div>
      </div>
    </div>
  );
};

// Main Component
export function STTRealtime({
  openFileDialog,
  variant,
}: {
  openFileDialog?: () => void;
  variant?: "default" | "simple" | "sidebar";
}) {
  const t = useTranslations("ClientKeys.SpeechToText.realtime");

  const {
    status,
    setStatus,
    committedTranscript,
    setCommittedTranscript,
    partialTranscript,
    setPartialTranscript,
    startRecording,
    stopRecording,
  } = useSTTRealtime();

  const isFinished = status === "finished";
  const isIdle = status === "idle";
  const isSessionLimitReached = status === "session_limit_reached";
  const isQuotaExceeded = status === "quota_exceeded";

  const handleFinish = () => {
    stopRecording();
    setStatus("finished");
  };

  const handleBackToHome = () => {
    setStatus("idle");
    setCommittedTranscript("");
    setPartialTranscript("");
  };

  const handleCloseSessionLimit = () => {
    setStatus("finished");
  };

  // Render idle/home screen
  if (isIdle) {
    return (
      <div
        className={clsx(
          "m-14 flex flex-1 flex-col overflow-hidden",
          variant === "simple" ? "h-full" : "rounded-inxl bg-default",
        )}
      >
        {/* Main content area - fills available space */}
        <div className='flex flex-1 flex-col items-center justify-center'>
          <button onClick={startRecording} className='group flex flex-col items-center gap-16'>
            <div className='relative flex h-48 w-48 items-center justify-center overflow-hidden rounded-full bg-darkest transition-transform group-hover:scale-105'>
              <Noise />
              <MicIcon className='z-2 h-20 w-20 text-inverse' />
            </div>
            <div className='text-center'>
              <p className='tw-text-2xl mb-4'>{t("click_to_start")}</p>
              <p className='f-paragraph-02 text-light'>{t("experience_power")}</p>
            </div>
          </button>
        </div>

        {/* Footer */}
        {openFileDialog && (
          <div className='flex items-center justify-between px-16 py-16'>
            <Button
              onClick={openFileDialog}
              variant='elevated'
              size='md'
              icon={<CircleArrowUp size={16} />}
              iconPosition={ICON_ALIGN_OPTIONS.BEFORE}
            >
              {t("upload_recording")}
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Render quota exceeded screen
  if (isQuotaExceeded) {
    return (
      <div
        className={clsx(
          "m-14 flex flex-1 flex-col overflow-hidden",
          variant === "simple" ? "h-full" : "rounded-inxl bg-default",
        )}
      >
        <LimitReachedOverlay
          rounded
          description={t("quota_exceeded_description")}
          title={t("quota_exceeded_title")}
          variant='redesigned'
          ctaLink={appUrls.speechToText}
        />
      </div>
    );
  }

  // Render recording/transcription UI
  return (
    <div
      className={clsx(
        "m-14 flex flex-1 flex-col overflow-hidden",
        variant === "simple" ? "h-full" : "rounded-inxl bg-default",
      )}
    >
      {/* Main content area - Transcription Display fills available space */}
      <div className='flex-1 overflow-hidden' style={{ borderRadius: "24px" }}>
        {isSessionLimitReached ? (
          <SessionLimitMessage onClose={handleCloseSessionLimit} />
        ) : (
          <TranscriptDisplay
            committedTranscript={committedTranscript}
            partialTranscript={partialTranscript}
            status={status}
          />
        )}
      </div>

      {/* Footer - Status and Action buttons */}
      <div className='flex items-center justify-between px-16 py-16'>
        {/* Left side - Status Indicator (hidden when finished or session limit reached) */}
        {!isFinished && !isSessionLimitReached ? <StatusIndicator status={status} /> : <div />}

        {/* Right side - Action Button */}
        {isFinished || isSessionLimitReached ? (
          <Button aria-label='Back to home' onClick={handleBackToHome} variant='elevated' size='md'>
            {t("new_transcription")}
          </Button>
        ) : (
          <Button aria-label='Finish transcribing' onClick={handleFinish} variant='primary' size='md'>
            {t("finish")}
          </Button>
        )}
      </div>
    </div>
  );
}
